class GiftRedemption extends HTMLElement {
  constructor() {
    super();
    this.boxes = null;
    this.code = null;
    this.selectedFrequency = 1;
    this.isLoading = false;
    this.isRum = false;

    // DOM elements - Code Input
    this.codeInputSection = null;
    this.codeForm = null;
    this.codeInput = null;
    this.codeErrorMessage = null;
    this.codeSubmitButton = null;
    this.codeLoadingSpinner = null;

    // DOM elements - Frequency Selection
    this.frequencySection = null;
    this.frequencyTabs = null;
    this.frequencyTabsMobile = null;
    this.frequencySubmitButton = null;
    this.frequencyLoadingSpinner = null;
    this.titleElement = null;
    this.descriptionElement = null;
    this.ginImage = null;
    this.rumImage = null;

    // Sign up header
    this.signUpHeader = null;
  }

  connectedCallback() {
    this.initializeDOM();
    this.bindEvents();
    this.autoInsertCode();
  }

  initializeDOM() {
    // Code input elements
    this.codeInputSection = this.querySelector('.gift-code-input__section');
    this.codeForm = this.querySelector('.gift-code-input__form');
    this.codeInput = this.querySelector('.gift-code-input__form input[name="gift_code"]');
    this.codeErrorMessage = this.querySelector('.error-message');
    this.codeSubmitButton = this.querySelector('.gift-code-input__form button[type="submit"]');
    this.codeLoadingSpinner = this.codeSubmitButton?.querySelector('.loading__spinner');

    // Frequency selection elements
    this.frequencySection = this.querySelector('.gift-frequency-selection__section');
    this.frequencyTabs = this.querySelector('.gift-frequency-tabs');
    this.frequencyTabsMobile = this.querySelector('.gift-frequency-tabs-mobile');
    this.frequencySubmitButton = this.querySelector('button[data-action="complete-setup"]');
    this.frequencyLoadingSpinner = this.frequencySubmitButton?.querySelector('.loading__spinner');
    this.titleElement = this.querySelector('.frequency-title');
    this.descriptionElement = this.querySelector('.description-text');
    this.ginImage = this.querySelector('.gin-image');
    this.rumImage = this.querySelector('.rum-image');
  }

  bindEvents() {
    // Code form events
    if (this.codeForm) {
      this.codeForm.addEventListener('submit', this.handleCodeSubmit.bind(this));
    }

    if (this.codeInput) {
      this.codeInput.addEventListener('input', this.hideCodeError.bind(this));
    }

    // Frequency tab events
    this.querySelectorAll('.frequency-tab').forEach((tab) => {
      tab.addEventListener('click', this.handleFrequencyChange.bind(this));
    });

    // Frequency submit button event
    if (this.frequencySubmitButton) {
      this.frequencySubmitButton.addEventListener('click', this.handleFrequencySubmit.bind(this));
    }
  }

  // Code Input Methods
  hideCodeError() {
    if (this.codeErrorMessage) {
      this.codeErrorMessage.style.display = 'none';
    }
  }

  showCodeError(message) {
    if (this.codeErrorMessage) {
      this.codeErrorMessage.textContent = message || this.codeErrorMessage.textContent;
      this.codeErrorMessage.style.display = 'block';
    }
  }

  setCodeLoading(loading) {
    this.isLoading = loading;

    if (this.codeSubmitButton) {
      this.codeSubmitButton.disabled = loading;
    }

    if (this.codeLoadingSpinner) {
      this.codeLoadingSpinner.style.display = loading ? 'inline-block' : 'none';
    }
  }

  async handleCodeSubmit(event) {
    event.preventDefault();

    if (this.isLoading) return;

    const code = this.codeInput?.value?.trim().toUpperCase();

    if (!code) {
      this.showCodeError('Please enter a gift code');
      return;
    }

    if (!code.startsWith('GIFT-')) {
      this.showCodeError();
      return;
    }

    this.hideCodeError();
    this.setCodeLoading(true);

    try {
      const isValid = await this.validateCode(code);
      if (isValid) {
        this.proceedToFrequencySelection();
      }
    } catch (error) {
      console.error('Error validating gift code:', error);
      this.showCodeError();
    } finally {
      this.setCodeLoading(false);
    }
  }

  async validateCode(code) {
    // Check if we have access to the CGC API configuration
    if (!window.ENV_VARS?.cgcApiUrl) {
      console.error('CGC API URL not configured');
      this.showCodeError('Service configuration error. Please try again later.');
      return false;
    }

    try {
      const response = await fetch(`${window.ENV_VARS.cgcApiUrl}/public/recharge/discount/?discount_code=${code}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const discount = data.discount;

      if (!discount || discount.status !== 'enabled' || Number(discount.times_used) > 0) {
        this.showCodeError();
        return false;
      }

      // Store validated code data
      this.code = code;
      this.boxes = discount.duration_usage_limit;
      this.isRum = code.toLowerCase().includes('-r-');

      return true;
    } catch (error) {
      console.error('API request failed:', error);
      this.showCodeError('Unable to verify code. Please check your connection and try again.');
      return false;
    }
  }

  proceedToFrequencySelection() {
    // Hide code input section
    if (this.codeInputSection) {
      this.codeInputSection.style.display = 'none';
    }

    // Show frequency selection section
    if (this.frequencySection) {
      this.frequencySection.style.display = 'block';
    }

    // Setup frequency selection
    this.setupFrequencySelection();

    // Scroll to top of frequency section
    if (this.frequencySection) {
      this.frequencySection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  setupFrequencySelection() {
    this.updateUIForGiftType();

    // Show frequency tabs only if multiple boxes
    if (parseInt(this.boxes) > 1) {
      if (this.frequencyTabs) this.frequencyTabs.style.display = 'flex';
      if (this.frequencyTabsMobile) this.frequencyTabsMobile.style.display = 'flex';
    }
  }

  updateUIForGiftType() {
    if (this.isRum) {
      // Update title
      if (this.titleElement) {
        const block = this.frequencySection;
        const rumTitle = block?.dataset.rumTitle;
        if (rumTitle) {
          this.titleElement.textContent = rumTitle;
        } else {
          this.titleElement.textContent = this.titleElement.textContent.replace('Gin', 'Rum');
        }
      }

      // Update description
      if (this.descriptionElement) {
        const block = this.frequencySection;
        const rumDescription = block?.dataset.rumDescription;
        if (rumDescription) {
          this.descriptionElement.innerHTML = rumDescription;
        } else {
          this.descriptionElement.innerHTML = this.descriptionElement.innerHTML.replace(/gin/gi, 'rum');
        }
      }

      // Show rum image, hide gin image
      if (this.ginImage) this.ginImage.style.display = 'none';
      if (this.rumImage) this.rumImage.style.display = 'block';
    } else {
      // Show gin image, hide rum image
      if (this.rumImage) this.rumImage.style.display = 'none';
      if (this.ginImage) this.ginImage.style.display = 'block';
    }
  }

  // Frequency Selection Methods
  handleFrequencyChange(event) {
    const frequency = parseInt(event.target.dataset.frequency);
    this.selectedFrequency = frequency;

    // Update active state on all tabs
    this.querySelectorAll('.frequency-tab').forEach((tab) => {
      tab.classList.toggle('active', parseInt(tab.dataset.frequency) === frequency);
    });
  }

  setFrequencyLoading(loading) {
    this.isLoading = loading;

    if (this.frequencySubmitButton) {
      this.frequencySubmitButton.disabled = loading;
    }

    if (this.frequencyLoadingSpinner) {
      this.frequencyLoadingSpinner.style.display = loading ? 'inline-block' : 'none';
    }
  }

  async handleFrequencySubmit() {
    if (this.isLoading) return;

    this.setFrequencyLoading(true);

    try {
      if (this.code.startsWith('GIFT-')) {
        const productHandle = this.isRum ? 'rotm' : 'gotm';

        const onboardingProductHandle = this.selectedFrequency === 1
          ? productHandle
          : `${productHandle}-${this.selectedFrequency}`;

        const signupConfig = {
          mainProduct: {
            handle: onboardingProductHandle,
          },
          discountCode: this.code,
        };

        const subscriptionCart = new SubscriptionCart();
        await subscriptionCart.checkout(signupConfig, true);
      } else {
        // Fallback for other codes
        const membershipType = this.selectedFrequency === 1 ? 'membership_monthly' : 'membership_bi_monthly';
        const productType = this.isRum ? 'rotm' : 'gotm';
        const url = `/pages/join?product=${productType}&type=${membershipType}&discount_code=${this.code}`;
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error processing checkout:', error);
      alert('There was an error processing your request. Please try again.');
    } finally {
      this.setFrequencyLoading(false);
    }
  }

  autoInsertCode() {
    const discountCode = new URL(window.location.href).searchParams.get('discount_code');
    if (!discountCode) {
      return;
    }

    this.codeInput.value = discountCode;
  }
}

// Register the custom element
if (!customElements.get('gift-redemption')) {
  customElements.define('gift-redemption', GiftRedemption);
}
